/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { DialogBoxComponent } from '../../shared/dialog-box/dialog-box.component';
import { EnumToArrayPipe } from '../../shared/pipes/enumToArray.pipe';
import { SelectionModel } from '@angular/cdk/collections';
import { LoaderService } from '../../sharedServices/loader.service';
import { FittingSizeManagementService } from '../../sharedServices/fitting-size-management.service';
import { switchMap, map } from 'rxjs/operators';
import { RoleDto, PageRoleRelationshipDto, UserDto, PageDto } from 'api/models';
import { UserService } from 'api/services';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-user-settings-page',
  templateUrl: './user-settings-page.component.html',
  styleUrls: ['./user-settings-page.component.css'],
  providers: [EnumToArrayPipe]
})
export class UserSettingsPageComponent implements OnInit {
  userRolesList = Object.keys(RoleDto);
  selectedRoleForAssignRole: string;
  selectedRoleForAssignPage: string;
  selectedUsersIDs: string;
  selectedPages: string;
  selectedPagesIds: string;
  refreshNavigation = new Subject();
  mySubscription: any;
  userRolesAndPages: PageRoleRelationshipDto[];
  users: UserDto[];
  pages: PageDto[];
  pagesToAssign: PageDto[] = [];
  pagesToRevoke: PageRoleRelationshipDto[] = [];
  usersToUpdate: UserDto[] = [];

  constructor(
    public dialog: MatDialog,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly loaderService: LoaderService,
    private readonly fittingSizeManagementService: FittingSizeManagementService
  ) {
    this.loaderService.display(true);
  }
  rightsToRolescolumnHeader = { status: 'Status', pages: 'Pages', role: 'Roles' };

  pagesColumnHeader = { status: 'Status', pageTitle: 'Pages' };

  userRolesHeader = {
    status: 'Status',
    fullName: 'Full Name',
    email: 'Email',
    role: 'Role'
  };

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    this.getAllUsers();
  }

  getAllUsers() {
    this.userService.getAllUsers().subscribe(result => {
      this.users = result;
      this.loaderService.display(false);
    });
  }

  getUserPagesAndRoles() {
    this.userService.getPageRoleRelationship()
      .pipe(map(result => {
        return result;
      }), switchMap(rolesAndPages => {
        this.userRolesAndPages = rolesAndPages.filter(x => x.role === this.selectedRoleForAssignPage);
        return this.userService.getAllPages();
      })).subscribe(result => {
        this.pages = result.filter((x) => {
          return !this.userRolesAndPages.some((f) => {
            this.loaderService.display(false);
            return f.pages === x.rtPageString;
          });
        });
      });


  }

  setSelectedRoleForAssignPage(role: string) {
    this.selectedRoleForAssignPage = role;
    this.loaderService.display(true);
    this.getUserPagesAndRoles();

  }

  setSelectedRoleForAssignRole(role: string) {
    this.selectedRoleForAssignRole = role;
  }

  isRoleSelectedForAssignRole(): boolean {
    return this.selectedRoleForAssignRole && this.usersToUpdate.length > 0
      ? false
      : true;
  }

  hasSelectedPagesIds() {
    return this.pagesToRevoke.length > 0 ? false : true;
  }

  isRoleSelectedForAssignPage(): boolean {
    return this.selectedRoleForAssignPage && this.pagesToAssign.length > 0
      ? false
      : true;
  }

  onAssignPagesToRoleCheckboxClick(element: SelectionModel<any>) {
    this.pagesToAssign = element.selected;
  }
  onRevokePagesToRoleCheckboxClick(element: SelectionModel<any>) {
    this.pagesToRevoke = element.selected;
  }
  onUpdateUserRoleCheckboxClick(element: SelectionModel<UserDto>) {
    this.usersToUpdate = element.selected;
  }
  assignPages() {
    this.userService.addNewPageRoleRelationship({
      body: this.pagesToAssign,
      role: this.selectedRoleForAssignPage as RoleDto
    }).subscribe(() => {
      this.refresh();
      this.fittingSizeManagementService.openSnackBar(
        'Please logout and login again to see the changes '
      );
    });
  }
  revokePages() {
    this.userService.deletePageRoleRelationship({ body: this.pagesToRevoke }).subscribe(() => {
      this.refresh();
      this.fittingSizeManagementService.openSnackBar(
        'Please logout and login again to see the changes '
      );
    });
  }

  assignRoles() {
    this.userService.updateUserRoleAsync({
      body: this.usersToUpdate,
      role: this.selectedRoleForAssignRole as RoleDto
    }).subscribe(() => {
      this.refresh();
    });
  }

  onAssignPagesClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Assign pages to role',
        message:
          'Are you sure you want to assign this selected page/pages? In order for the changes to take effect you will need to log in again.'
      }
    });
    this.handleAssignPagesDialogButtons(dialogRef);
  }

  onRevokePagesClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Revoke pages to role',
        message:
          'Are you sure you want to revoke this selected page/pages? In order for the changes to take effect you will need to log in again.'
      }
    });
    this.handleRevokePagesDialogButtons(dialogRef);
  }

  onChangeRoleForUserClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Change role of user',
        message:
          // tslint:disable-next-line: max-line-length
          'Are you sure you want to change roles for selected user/users? In order for the changes to take effect you will need to log in again.'
      }
    });
    this.handleChangeRoleDialogButtons(dialogRef);
  }

  handleAssignPagesDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        this.assignPages();

      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  handleRevokePagesDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        this.revokePages();
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  handleChangeRoleDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        this.assignRoles();
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }



}
